// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, query, where, doc, getDoc, getDocs, collectionGroup, onSnapshot } from 'firebase/firestore';

// Root level collections and documents
export const USERS_COLLECTION_PATH = 'users';
export const USER_DOCUMENT_PATH = (userId) => `users/${userId}`;
export const CLIENTS_COLLECTION_PATH = 'clients';
export const MESSAGES_COLLECTION_PATH = `messages`;
export const DEVICE_PARAMETERS_CONFIG_DOCUMENT_PATH = 'settings/device_parameters_config';
export const DELIVERIES_SETTINGS_GLOBAL_PATH = `settings/deliveries_settings_global`;
export const LICENSE_AGREEMENTS_COLLECTION_PATH = 'legal/client_agreements/license_agreements';
export const GENERAL_TERMS_AND_CONDITIONS_COLLECTION_PATH = 'legal/client_agreements/general_terms_and_conditions';
export const EVENT_LOGS_COLLECTION_PATH = 'event_logs';
export const ML_MODELS_COLLECTION_PATH = 'ML_models';

// General client pahts
export const CLIENT_DOCUMENT_PATH = (clientId) => `clients/${clientId}`;
export const PAIRWISE_RANKING_COLLECTION_PATH = (clientId) => `clients/${clientId}/pairwise_ranking`;
export const PAIRWISE_RANKING_SCORES_DOCUMENT_PATH = (clientId, rankingSetId) =>
  `clients/${clientId}/pairwise_ranking/${rankingSetId}/scores/scores`;
export const PAIRWISE_RANKING_GAMES_DOCUMENT_PATH = (clientId, rankingSetId, gameId) =>
  `clients/${clientId}/pairwise_ranking/${rankingSetId}/games/${gameId}`;
export const SUPPLIERS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_collections/suppliers`;
export const SUPPLIER_DETAILS_COLLECTION_PATH = (clientId) =>
  `clients/${clientId}/client_collections/suppliers/supplier_details`;
export const LABELLING_STATISTICS_COLLECTION_PATH = (clientId) => `clients/${clientId}/labelling_statistics`;
export const LABELLING_STATISTICS_DOCUMENT_PATH = (clientId, dateString) =>
  `clients/${clientId}/labelling_statistics/${dateString}`;

// Client delivery paths
export const DELIVERIES_COLLECTION_PATH = (clientId) => `clients/${clientId}/deliveries`;
export const DELIVERY_DOCUMENT_PATH = (clientId, deliveryId) => `clients/${clientId}/deliveries/${deliveryId}`;
export const DELIVERY_BALES_COLLECTION_PATH = (clientId, deliveryId) =>
  `clients/${clientId}/deliveries/${deliveryId}/bales`;
export const BALE_DOCUMENT_PATH = (clientId, deliveryId, baleId) =>
  `clients/${clientId}/deliveries/${deliveryId}/bales/${baleId}`;
export const OBSERVATIONS_COLLECTION_PATH = (clientId) => `clients/${clientId}/observations`;
export const OBSERVATION_DOCUMENT_PATH = (clientId, observationId) =>
  `clients/${clientId}/observations/${observationId}`;
export const STOCK_FIELDS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_collections/stock_fields`;
export const DELIVERY_IMAGES_COLLECTION_PATH = (clientId, deliveryId) =>
  `clients/${clientId}/deliveries/${deliveryId}/delivery_images`;
export const DELIVERY_IMAGE_DOCUMENT_PATH = (clientId, deliveryId, imageId) =>
  `clients/${clientId}/deliveries/${deliveryId}/delivery_images/${imageId}`;
export const SCANNED_DOCUMENTS_COLLECTION_PATH = (clientId, deliveryId) =>
  `clients/${clientId}/deliveries/${deliveryId}/scanned_documents`;
export const SCANNED_DOCUMENT_DOCUMENT_PATH = (clientId, deliveryId, docId) =>
  `clients/${clientId}/deliveries/${deliveryId}/scanned_documents/${docId}`;

// Client device paths
export const DEVICES_COLLECTION_PATH = (clientId) => `clients/${clientId}/devices`;
export const DEVICE_DOCUMENT_PATH = (clientId, deviceId) => `clients/${clientId}/devices/${deviceId}`;
export const DEVICE_BALE_FEED_TIMESTAMPS_COLLECTION_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/bale_feed_timestamps`;
export const DEVICE_MATERIAL_FLOW_TIMESTAMPS_COLLECTION_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/material_flow_timestamps`;
export const DEVICE_VOLUMES_DOCUMENT_PATH = (clientId, deviceId, volumeDocId) =>
  `clients/${clientId}/devices/${deviceId}/volumes/${volumeDocId}`;
export const DEVICE_STATUS_DOCUMENT_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/status_logs_aggregated/status`;
export const DEVICE_MATERIAL_FLOW_BBOX_LABELS_COLLECTION_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/material_flow_bbox_labels`;
export const DEVICE_MATERIAL_FLOW_BBOX_LABELS_DOCUMENT_PATH = (clientId, deviceId, labelId) =>
  `clients/${clientId}/devices/${deviceId}/material_flow_bbox_labels/${labelId}`;
export const DEVICE_VOLUME_TRACKING_DOCUMENT_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/volumes/volume_tracking`;
export const DEVICE_VOLUME_TRACKING_AGGREGATIONS_DAILY_COLLECTION_PATH = (clientId, deviceId) =>
  `clients/${clientId}/devices/${deviceId}/volumes/volume_tracking/aggregations_daily`;

// Client settings paths
export const DELIVERY_SETTINGS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_settings/deliveries`;
export const CLAIM_SETTINGS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_settings/claim_settings`;
export const BALE_FEED_SETTINGS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_settings/bale_feed_settings`;
export const DASHBOARD_SETTINGS_DOCUMENT_PATH = (clientId) => `clients/${clientId}/client_settings/dashboard_settings`;

// Client analytics paths
export const AVAILABLE_DELIVERY_ANALYTICS_DOCUMENT_PATH = (clientId) =>
  `clients/${clientId}/client_analytics/available_labelsets_delivery_bales`;

// Collection Groups
export const DEVICES_COLLECTION_GROUP = 'devices';
export const BALE_FEED_TIMESTAMPS_COLLECTION_GROUP = 'bale_feed_timestamps';

// Storage paths
export const SCANNED_DOCUMENT_STORAGE_PATH = (clientId, timestamp) =>
  `clients/${clientId}/deliveries/scanned_documents/${timestamp}.jpg`;

/**
 * Get comprehensive user information including company and device data
 * @param {Firestore} firestore - The firestore instance
 * @param {string} uid - The user's unique identifier
 * @returns {Object} - The user information object containing info, company, devices, and device_id
 */
export async function getUserInfo(firestore, uid) {
  const userInfo = {};

  // Fetch user company & language
  let userDocSnap;
  try {
    userDocSnap = await getDoc(doc(firestore, USERS_COLLECTION_PATH, uid));
  } catch (error) {
    console.error('Error fetching user info:', error);
    return {};
  }

  if (!userDocSnap.exists()) {
    return;
  }
  userInfo['info'] = userDocSnap.data();
  const company = userDocSnap.data().company;

  // Fetch company display name
  let clientDocSnap;
  try {
    clientDocSnap = await getDoc(doc(firestore, CLIENTS_COLLECTION_PATH, company));
  } catch (error) {
    console.error('Error fetching client info:', error);
    return {};
  }

  if (clientDocSnap.exists()) {
    userInfo['company'] = clientDocSnap.data();
  }

  // Fetch device
  const deviceQuery = query(collection(firestore, DEVICES_COLLECTION_PATH(company)), where('client', '==', company));
  let querySnapshot;
  try {
    querySnapshot = await getDocs(deviceQuery);
  } catch (error) {
    console.error('Error fetching devices:', error);
    return {};
  }

  if (querySnapshot.empty) {
    userInfo['device_id'] = null;
    return userInfo;
  }
  userInfo['devices'] = {};
  for (const doc of querySnapshot.docs) {
    userInfo['devices'][doc.id] = doc.data();
  }

  if (userInfo.info.selected_device_id && userInfo.info.selected_device_id in userInfo.devices) {
    userInfo['device_id'] = userInfo.info.selected_device_id;
  } else {
    userInfo['device_id'] = querySnapshot.docs[0].data().device_id;
  }

  return userInfo;
}

/**
 * Get all clients
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The clients data
 */
export async function getClients(firestore) {
  const clients = {};

  let clientsSnapshot;
  try {
    const clientsRef = collection(firestore, CLIENTS_COLLECTION_PATH);
    clientsSnapshot = await getDocs(clientsRef);
  } catch (error) {
    console.error('Error fetching clients:', error);
    return {};
  }

  clientsSnapshot.forEach((doc) => {
    clients[doc.id] = { ...doc.data() };
  });

  return clients;
}

/**
 * Get all devices for all clients
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The devices data
 */
export async function getAllDevices(firestore) {
  const devices = {};

  let devicesSnapshot;
  try {
    const devicesRef = collectionGroup(firestore, DEVICES_COLLECTION_GROUP);
    devicesSnapshot = await getDocs(devicesRef);
  } catch (error) {
    console.error('Error fetching devices:', error);
    return {};
  }

  devicesSnapshot.forEach((doc) => {
    devices[doc.id] = { ...doc.data() };
  });

  return devices;
}

/**
 * Get all devices for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The client company identifier
 * @returns {Object} - The devices data
 */
export async function getDevicesForClient(firestore, clientId) {
  const devices = {};

  let devicesSnapshot;
  try {
    const devicesRef = collection(firestore, DEVICES_COLLECTION_PATH(clientId));
    devicesSnapshot = await getDocs(devicesRef);
  } catch (error) {
    console.error('Error fetching devices:', error);
    return {};
  }

  devicesSnapshot.forEach((doc) => {
    devices[doc.id] = { ...doc.data() };
  });

  return devices;
}

/**
 * Get device parameters configuration
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The device parameters configuration data
 */
export async function getParametersConfig(firestore) {
  let snapshot;
  try {
    const parametersConfigRef = doc(firestore, DEVICE_PARAMETERS_CONFIG_DOCUMENT_PATH);
    snapshot = await getDoc(parametersConfigRef);
  } catch (error) {
    console.error('Error fetching parameters config:', error);
    return {};
  }

  if (!snapshot.exists()) {
    return;
  }
  return snapshot.data();
}

/**
 * Get delivery settings for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The delivery settings data
 */
export async function getDeliverySettings(firestore, company) {
  let deliverySettings = {};

  let deliverySettingsSnapshot;
  try {
    const deliverySettingsRef = doc(firestore, DELIVERY_SETTINGS_DOCUMENT_PATH(company));
    deliverySettingsSnapshot = await getDoc(deliverySettingsRef);
  } catch (error) {
    console.error('Error fetching delivery settings:', error);
    return {};
  }

  if (deliverySettingsSnapshot.exists()) {
    deliverySettings = deliverySettingsSnapshot.data();
  }

  return deliverySettings;
}

/**
 * Subscribe to delivery settings for a client (realtime listener)
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @param {Function} callback - Callback function receiving the delivery settings data
 * @returns {Function} - Unsubscribe function
 */
export function subscribeToDeliverySettings(firestore, company, callback) {
  if (!company) return () => {};

  const deliverySettingsRef = doc(firestore, DELIVERY_SETTINGS_DOCUMENT_PATH(company));
  return onSnapshot(
    deliverySettingsRef,
    (docSnap) => {
      callback(docSnap.exists() ? docSnap.data() : {});
    },
    (error) => {
      console.error('Error subscribing to delivery settings:', error);
      callback({});
    },
  );
}

/**
 * Get claim settings for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The claim settings data
 */
export async function getClaimSettings(firestore, company) {
  let claimSettings = {};

  let claimSettingsSnapshot;
  try {
    const claimSettingsRef = doc(firestore, CLAIM_SETTINGS_DOCUMENT_PATH(company));
    claimSettingsSnapshot = await getDoc(claimSettingsRef);
  } catch (error) {
    console.error('Error fetching claim settings:', error);
    return {};
  }

  if (claimSettingsSnapshot.exists()) {
    claimSettings = claimSettingsSnapshot.data();
  }

  return claimSettings;
}

/**
 * Get bale feed settings for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The bale feed settings data
 */
export async function getBaleFeedSettings(firestore, company) {
  let baleFeedSettings = {};

  let baleFeedSettingsSnapshot;
  try {
    const baleFeedSettingsRef = doc(firestore, BALE_FEED_SETTINGS_DOCUMENT_PATH(company));
    baleFeedSettingsSnapshot = await getDoc(baleFeedSettingsRef);
  } catch (error) {
    console.error('Error fetching bale feed settings:', error);
    return {};
  }

  if (baleFeedSettingsSnapshot.exists()) {
    baleFeedSettings = baleFeedSettingsSnapshot.data();
  }

  return baleFeedSettings;
}

/**
 * Get dashboard settings for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The dashboard settings data
 */
export async function getDashboardSettings(firestore, company) {
  let dashboardSettings = {};

  let dashboardSettingsSnapshot;
  try {
    const dashboardSettingsRef = doc(firestore, DASHBOARD_SETTINGS_DOCUMENT_PATH(company));
    dashboardSettingsSnapshot = await getDoc(dashboardSettingsRef);
  } catch (error) {
    console.error('Error fetching dashboard settings:', error);
    return {};
  }

  if (dashboardSettingsSnapshot.exists()) {
    dashboardSettings = dashboardSettingsSnapshot.data();
  }

  return dashboardSettings;
}

/**
 * Get all required license agreements
 * @param {Firestore} firestore - The firestore instance
 * @returns {string[]} - Array of required license agreement IDs
 */
export async function getRequiredLicenseAgreements(firestore) {
  let snapshot;
  try {
    const licenseAgreementsRef = collection(firestore, LICENSE_AGREEMENTS_COLLECTION_PATH);
    const q = query(licenseAgreementsRef, where('required', '==', true));
    snapshot = await getDocs(q);
  } catch (error) {
    console.error('Error fetching required license agreements:', error);
    return [];
  }

  const requiredLicenses = [];
  snapshot.forEach((doc) => {
    requiredLicenses.push(doc.id);
  });

  return requiredLicenses;
}

/**
 * Get suppliers for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The suppliers data summary
 */
export async function getSuppliers(firestore, company) {
  let suppliers = {};

  let suppliersSnapshot;
  try {
    const suppliersRef = doc(firestore, SUPPLIERS_DOCUMENT_PATH(company));
    suppliersSnapshot = await getDoc(suppliersRef);
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    return {};
  }

  if (suppliersSnapshot.exists()) {
    suppliers = suppliersSnapshot.data()?.summary || {};
  }

  return suppliers;
}

export function subscribeToSuppliers(firestore, company, callback) {
  const suppliersRef = doc(firestore, SUPPLIERS_DOCUMENT_PATH(company));

  // onSnapshot sets up the listener and returns the unsubscribe function
  const unsubscribe = onSnapshot(
    suppliersRef,
    (suppliersSnapshot) => {
      let suppliersData = {};
      if (suppliersSnapshot.exists()) {
        suppliersData = suppliersSnapshot.data()?.summary || {};
      }
      // Call the callback to pass the updated data to the store
      callback(suppliersData);
    },
    (error) => {
      console.error('Error fetching suppliers:', error);
    },
  );

  // We return the unsubscribe function so the store can clean up the listener later
  return unsubscribe;
}

/**
 * Get stock fields for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @returns {Object} - The stock fields data
 */
export async function getStockFields(firestore, company) {
  let stockFields = {};

  let stockFieldsSnapshot;
  try {
    const stockFieldsRef = doc(firestore, STOCK_FIELDS_DOCUMENT_PATH(company));
    stockFieldsSnapshot = await getDoc(stockFieldsRef);
  } catch (error) {
    console.error('Error fetching stock fields:', error);
    return {};
  }

  if (stockFieldsSnapshot.exists()) {
    stockFields = stockFieldsSnapshot.data()?.fields || {};
  }

  return stockFields;
}

/**
 * Subscribe to stock fields for a client and call callback with the data when it changes
 * @param {Firestore} firestore - The firestore instance
 * @param {string} company - The client company identifier
 * @param {Function} callback - The callback function to call when the stock fields change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToStockFields(firestore, company, callback) {
  const stockFieldsRef = doc(firestore, STOCK_FIELDS_DOCUMENT_PATH(company));

  // onSnapshot sets up the listener and returns the unsubscribe function
  const unsubscribe = onSnapshot(
    stockFieldsRef,
    (stockFieldsSnapshot) => {
      let stockFieldsData = {};
      if (stockFieldsSnapshot.exists()) {
        stockFieldsData = stockFieldsSnapshot.data()?.fields || {};
      }
      // Call the callback to pass the updated data to the store
      callback(stockFieldsData);
    },
    (error) => {
      console.error('Error fetching stock fields:', error);
    },
  );

  // We return the unsubscribe function so the store can clean up the listener later
  return unsubscribe;
}
